import styled, { css } from "styled-components";
import { FC, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Control,
  UseFormGetValues,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";

import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { ActionsWrap } from "@/components/Table";
import Text from "@/components/Text";
import ParticipantBlock from "./ParticipantBlock";
import { EventResource } from "@/types/resources";
import Button from "@/components/Button";
import { api } from "@/utils/api";
import useAlerts from "@/hooks/useAlerts";
import FormField from "@/components/Forms/FormField";
import { FormDataT } from "@/pages/ProductionEvent";
import { EventsStatusEnum } from "@/utils/enums";
import MeetingRooms from "./MeetingRooms";

const eventsDurations = [
  { label: "30 min", value: 30 },
  { label: "1 h", value: 60 },
  { label: "1 h 30 min", value: 90 },
  { label: "2 h", value: 120 },
  { label: "2 h 30 min", value: 150 },
  { label: "3 h", value: 180 },
];

const Box = styled.div`
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  padding: 0 24px;
  margin-bottom: 32px;
`;

const BoxHeader = styled.div<{ $isSkipped?: boolean }>`
  padding: 24px 0;
  ${({ $isSkipped }) =>
    $isSkipped &&
    css`
      padding: 17px 0;
    `}
`;

const BoxContent = styled.div`
  padding: 24px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const BoxGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 16px;
`;

const BoxFooter = styled.div`
  padding: 24px 0;
  text-align: right;
`;

const StyledColorizedButton = styled(Button)`
  padding: 0 15px;
`;

const AvailableButton = styled(Button)`
  color: ${({ theme }) => theme.colors.success};
  background-color: #ecf9f1;
  padding: 0 15px;
  svg {
    margin-right: 8px;
  }
  &:disabled {
    color: ${({ theme }) => theme.colors.success};
    background-color: #ecf9f1;
  }
`;

const HeadingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InputRow = styled.div`
  margin-top: 32px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 16px;
  align-items: flex-start;
`;

const Inputs = styled.div`
  display: flex;
  gap: 16px;
  > div {
    width: 50%;
  }
`;

const DateInputs = styled.div`
  display: flex;
  gap: 16px;
  > div {
    width: 37.5%;
    &:first-child {
      width: 62.5%;
    }
  }
`;

const DescriptionHolder = styled.div`
  max-width: 390px;
`;

const EventDateHeading = styled(Text)`
  margin-bottom: 4px;
`;

const SectionHeading = styled(Text)`
  margin-bottom: 8px;
`;

interface Props {
  event: EventResource;
  control: Control<FormDataT, any>;
  index: number;
  getValues: UseFormGetValues<FormDataT>;
  status: EventsStatusEnum;
  setValue: UseFormSetValue<FormDataT>;
}

const ProjectEventBlock: FC<Props> = ({
  event,
  control,
  index,
  status,
  setValue,
}) => {
  const [availabilityErrors, setAvailabilityErrors] = useState<
    { participantId: number }[]
  >([]);

  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

  const [isAvailable, setIsAvailable] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onSkip = async () => {
    try {
      const confirm = await alerts.confirm(
        `Are you sure you want to skip this event?`,
        event.name
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(
        `/v1/production/events/${event.id}`
      );

      setValue(`events.${index}.deleted`, true);

      queryClient.invalidateQueries(["production_events_projects"], {
        exact: false,
      });

      alerts.success("Event was successfully skipped");
    } catch (e) {
      alerts.error();
    }
  };

  const onRestore = async () => {
    try {
      const confirm = await alerts.confirm(
        `Are you sure you want to restore this event?`,
        event.name
      );

      if (!confirm) return;

      await api.post<never, { id: number }>(
        `/v1/production/events/${event.id}`
      );

      setValue(`events.${index}.deleted`, false);

      queryClient.invalidateQueries(["production_events_projects"], {
        exact: false,
      });

      alerts.success("Event was successfully restored");
    } catch (e) {
      alerts.error();
    }
  };

  const eventStartsAt = useWatch({
    name: `events.${index}.starts_at`,
    control,
  });

  const eventDuration = useWatch({
    name: `events.${index}.duration`,
    control,
  });

  const locationType = useWatch({
    name: `events.${index}.location_type`,
    control,
  });

  const onCheckAvailability = async () => {
    try {
      setIsCheckingAvailability(true);

      const response = await api.post<
        never,
        { errors: { participantId: number }[] }
      >(`/v1/production/events/${event.id}/availability`, {
        starts_at: eventStartsAt,
        duration: eventDuration,
      });

      if (!response.errors.length) setIsAvailable(true);

      setValue(`events.${index}.availabilityChecked`, true);

      setIsCheckingAvailability(false);

      setAvailabilityErrors(response.errors);
    } catch (e) {
      alerts.error();
      setIsCheckingAvailability(false);
    }
  };

  useEffect(() => {
    setIsAvailable(false);
  }, [eventStartsAt, eventDuration]);

  return (
    <Box>
      <BoxHeader $isSkipped={!!event.deletedAt}>
        <HeadingRow>
          <Text $color={!!event.deletedAt ? "gray" : "black"} $type="button">
            {event.name}
          </Text>
          {event.deletedAt ? (
            <StyledColorizedButton
              disabled={status === EventsStatusEnum.SENT}
              color="colorized"
              onClick={onRestore}
            >
              Restore
            </StyledColorizedButton>
          ) : (
            <>
              {status === EventsStatusEnum.DRAFT && (
                <ActionsWrap>
                  <ActionsDropdown>
                    <ActionsDropdownButton as="button" onClick={onSkip}>
                      <span>
                        <svg
                          width="18"
                          height="16"
                          viewBox="0 0 18 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1.6875 8H12.9375"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M7.875 13.1431L12.9375 8.00028L7.875 2.85742"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M16.3125 13.7137V2.28516"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      Skip
                    </ActionsDropdownButton>
                  </ActionsDropdown>
                </ActionsWrap>
              )}
            </>
          )}
        </HeadingRow>
        {!event.deletedAt && (
          <InputRow>
            <div>
              <EventDateHeading $type="caption">Event details</EventDateHeading>
              <DescriptionHolder>
                <Text $type="smallHeadline" $color="gray">
                  Set the name, date, time and duration for {event.name} event
                  for all selected attendees and check availability of meeting
                  rooms.
                </Text>
              </DescriptionHolder>
            </div>
            <div>
              <FormField<FormDataT, number>
                disabled={status === EventsStatusEnum.SENT}
                label="Event Name"
                name={`events.${index}.calendar_event_name`}
                control={control}
                placeholder="Add Name"
              />
            </div>
          </InputRow>
        )}
      </BoxHeader>
      {!event.deletedAt && (
        <>
          <BoxContent>
            <Inputs>
              <DateInputs>
                <FormField<FormDataT>
                  disabled={status === EventsStatusEnum.SENT}
                  label="Date & Time"
                  type="datepicker"
                  name={`events.${index}.starts_at`}
                  control={control}
                />
                <FormField<FormDataT, number>
                  disabled={status === EventsStatusEnum.SENT}
                  label="Duration"
                  type="select"
                  options={eventsDurations}
                  name={`events.${index}.duration`}
                  control={control}
                />
              </DateInputs>
              <div>
                <FormField<FormDataT, string | null>
                  disabled={
                    status === EventsStatusEnum.SENT ||
                    !eventStartsAt ||
                    !eventDuration
                  }
                  label="Location"
                  type="select"
                  options={[
                    { label: "No location", value: null },
                    { label: "Youtube Studio", value: "youtube" },
                    { label: "Facebook Studio", value: "facebook" },
                  ]}
                  name={`events.${index}.location_type`}
                  control={control}
                />
              </div>
            </Inputs>
            <MeetingRooms
              eventStartsAt={eventStartsAt}
              eventDuration={eventDuration}
              locationType={locationType}
              setValue={setValue}
              meetingRoomSelected={
                !!event.meetingRoom && status === EventsStatusEnum.SENT
              }
              meetingRoom={event.meetingRoom}
              fieldIndex={index}
              eventStatus={status}
            />
            <SectionHeading $type="caption">Participants</SectionHeading>
            <BoxGrid>
              {event.participants.map((participant) => (
                <ParticipantBlock
                  removedFromMeet={participant.isDetached}
                  key={participant.id}
                  participant={participant}
                  eventId={event.id}
                  status={status}
                  unavailable={
                    !!availabilityErrors.find(
                      (error) => error.participantId === participant.id
                    )
                  }
                />
              ))}
            </BoxGrid>
          </BoxContent>
          <BoxFooter>
            {isAvailable ? (
              <AvailableButton disabled color="colorized">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.0002 18.3087C14.5884 18.3087 18.3079 14.5893 18.3079 10.0011C18.3079 5.41284 14.5884 1.69336 10.0002 1.69336C5.41199 1.69336 1.6925 5.41284 1.6925 10.0011C1.6925 14.5893 5.41199 18.3087 10.0002 18.3087Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.8079 7.92383L8.73094 12.77L6.1925 10.3469"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Ready to schedule
              </AvailableButton>
            ) : (
              <StyledColorizedButton
                onClick={onCheckAvailability}
                color="colorized"
                loading={isCheckingAvailability}
                disabled={status === EventsStatusEnum.SENT || !eventStartsAt}
              >
                {isCheckingAvailability ? "Checking" : "Check"} availability
              </StyledColorizedButton>
            )}
          </BoxFooter>
        </>
      )}
    </Box>
  );
};

export default ProjectEventBlock;
